import React, { useRef, useEffect, useState } from "react";
import { Group, Rect, Text, Image as KonvaImage } from "react-konva";

function lerp(a, b, t) {
  return a + (b - a) * t;
}

export default function FloorplanAnimatedButton({
  x,
  y,
  label,
  direction = "right",
  size = 48,
  padding = 16,
  fontSize = 32,
  isMobile,
  arrowImage,
  onClick
}) {
  const groupRef = useRef(null);
  const rectRef = useRef(null);
  const labelRef = useRef(null);
  const arrowGroupRef = useRef(null);
  const arrowImgRef = useRef(null);
  const labelClipGroupRef = useRef(null);

  const [hovered, setHovered] = useState(false);
  const [expandedWidth, setExpandedWidth] = useState(size);
  const [textOpacity, setTextOpacity] = useState(0);

  useEffect(() => {
    if (isMobile) {
      const timer = setTimeout(() => {
        setHovered(true);
      }, 1200);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  useEffect(() => {
    if (labelRef.current) {
      const textW = labelRef.current.width() || 0;
      setExpandedWidth(size + padding + textW + padding);
    }
  }, [label, size, padding]);

  useEffect(() => {
    if (groupRef.current && direction === "right") {
      groupRef.current.x(x + (expandedWidth - size));
    } else if (groupRef.current && direction === "left") {
      groupRef.current.x(x);
    }
  }, [expandedWidth, direction, x, size]);

  useEffect(() => {
    const layer = groupRef.current?.getLayer();
    if (!layer || !rectRef.current) return;

    const anim = new window.Konva.Animation(() => {
      if (!rectRef.current) return;
      const targetW = hovered ? expandedWidth : size;
      const currentW = rectRef.current.width();
      const newW = lerp(currentW, targetW, 0.05);
      rectRef.current.width(newW);

      if (labelClipGroupRef.current) {
        labelClipGroupRef.current.clip({
          x: -3,
          y: -3,
          width: newW + 6,
          height: size + 6
        });
      }

      if (arrowGroupRef.current) {
        arrowGroupRef.current.x(direction === "right" ? newW - size : 0);
      }

      if (groupRef.current) {
        if (direction === "right") {
          const targetX = hovered ? x : x + (expandedWidth - size);
          const gx = groupRef.current.x();
          groupRef.current.x(lerp(gx, targetX, 0.05));
        } else {
          groupRef.current.x(x);
        }
      }

      const progress = Math.min(
        Math.max((newW - size) / (expandedWidth - size), 0),
        1
      );
      setTextOpacity(progress);
    }, layer);

    anim.start();
    return () => anim.stop();
  }, [hovered, expandedWidth, size, direction, x]);

  const arrowSize = size * 0.65;

  return (
    <Group
      ref={groupRef}
      y={y}
      clip={{
        x: -3,
        y: -3,
        width: rectRef.current?.width() + 6 || size + 6,
        height: size + 6
      }}
      onMouseEnter={() => {
        setHovered(true);
        document.body.style.cursor = 'pointer';
      }}
      onMouseLeave={() => {
        setHovered(false);
        document.body.style.cursor = 'default';
      }}
      onClick={onClick}
      onTap={onClick}
    >
      <Rect
        ref={rectRef}
        width={size}
        height={size}
        cornerRadius={size}
        fill="rgba(255,255,255,0.1)"
        stroke="white"
        strokeWidth={2}
      />

      <Group
        ref={arrowGroupRef}
        x={0}
        y={0}
        width={size}
        height={size}
        listening={false}
      >
        {arrowImage && (
          <KonvaImage
            ref={arrowImgRef}
            image={arrowImage}
            x={(size - arrowSize) / 2}
            y={(size - arrowSize) / 2}
            width={arrowSize}
            height={arrowSize}
            listening={false}
          />
        )}
      </Group>

      <Text
        ref={labelRef}
        text={label}
        fontSize={fontSize}
        fill="white"
        y={size / 2 - fontSize / 2}
        x={direction === "left" ? size : padding * 1.5}
        opacity={textOpacity}
        listening={false}
      />
    </Group>
  );
}
